import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Positioned-overlay wrapper that anchors an indicator (dot or count badge)
 * on top of its child. Unifies the badge-positioning logic that previously
 * diverged between NotificationBell (corner overhang) and IconRailLayout
 * (inset, ringed).
 */
const indicatorPositionVariants = cva('absolute pointer-events-none', {
  variants: {
    position: {
      // Overhangs the child's top-right corner by ~50%.
      'top-right': 'top-0 right-0 -translate-y-1/2 translate-x-1/2',
      // Sits just inside the top-right corner; pair with `ring-2 ring-background`
      // on the indicator when the underlying surface is light.
      'inset-top-right': 'top-1 right-1',
    },
  },
  defaultVariants: {
    position: 'top-right',
  },
})

export interface IndicatorBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof indicatorPositionVariants> {
  /** The element being annotated (icon button, avatar, etc.). */
  children: React.ReactNode
  /** Dot or count badge to overlay. */
  indicator?: React.ReactNode
  /** Hide the indicator without unmounting the wrapper. Default true. */
  show?: boolean
  /** className applied to the positioned indicator span (not the wrapper). */
  indicatorClassName?: string
}

const IndicatorBadge = React.forwardRef<HTMLSpanElement, IndicatorBadgeProps>(
  ({ className, children, indicator, show = true, position, indicatorClassName, ...props }, ref) => (
    <span ref={ref} className={cn('relative inline-flex', className)} {...props}>
      {children}
      {show && indicator && (
        <span className={cn(indicatorPositionVariants({ position }), indicatorClassName)}>{indicator}</span>
      )}
    </span>
  ),
)
IndicatorBadge.displayName = 'IndicatorBadge'

export { IndicatorBadge, indicatorPositionVariants }
